import type { ComponentProps, MouseEvent, ReactElement } from "react";

import { TableRow } from "@/components/ui/table";
import { cn } from "@/lib/utils";

/** What answers a click itself; a click there is not a click on the row. */
const OWN_CLICK = "a, button, input, select, textarea, label, summary, [role='button']";

/**
 * A table row that leads where its first link leads (D48): a click anywhere in the row follows
 * that link, so the target stays defined once, in the link.
 *
 * The link stays the way for keyboard and screen reader; the row gets no `tabIndex` and no role.
 * A row onClick rather than a link stretched over the row: `position: relative` on a `<tr>` is
 * undefined in CSS 2.1, and where a browser ignores it, every overlay covers the whole table and
 * each click opens the last row. An overlay would also stop text from being marked.
 *
 * Marking by dragging stays possible; a double click to mark a word leaves with its first click
 * (accepted, D48: waiting for a second click would delay every row).
 */
export function LinkRow({ className, ...props }: ComponentProps<"tr">): ReactElement {
  return (
    <TableRow
      data-row-link=""
      className={cn("cursor-pointer", className)}
      onClick={followRowLink}
      onAuxClick={followRowLink}
      onMouseDown={(event) => {
        // The middle button would start the browser's autoscroll instead of the auxclick.
        if (event.button === 1 && !ownClick(event)) {
          event.preventDefault();
        }
      }}
      {...props}
    />
  );
}

function ownClick(event: MouseEvent<HTMLElement>): boolean {
  return event.target instanceof Element && event.target.closest(OWN_CLICK) !== null;
}

/** Follows the row's first link; a new tab for the middle button and the modifier keys. */
export function followRowLink(event: MouseEvent<HTMLElement>): void {
  if (event.button > 1 || ownClick(event)) {
    return;
  }
  const link = event.currentTarget.querySelector<HTMLAnchorElement>("a[href]");
  if (link === null) {
    return;
  }
  // A new tab asks for itself, whatever is marked: shift extends the marking on mousedown, and
  // the middle button never clears one.
  if (event.button === 1 || event.ctrlKey || event.metaKey || event.shiftKey) {
    window.open(link.href, "_blank", "noopener");
    return;
  }
  // The click that ends marking text in this row is no request to leave the page; a marking
  // elsewhere on the page is none of the row's business.
  // (`containsNode` will not do: a range wholly inside the row does not "contain" the row.)
  const selection = window.getSelection();
  const row = event.currentTarget;
  if (
    selection !== null &&
    !selection.isCollapsed &&
    (row.contains(selection.anchorNode) || row.contains(selection.focusNode))
  ) {
    return;
  }
  // The router's own link navigates, with its search and state.
  link.click();
}
